import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getAllPartners } from "@/Services/EventServices";
import {
  recordSponsorImpression,
  recordSponsorClick,
} from "@/lib/sponsorAnalytics";

export default function GlobalSponsorStrip({ variant = "footer" }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const response = await getAllPartners();
        if (response?.success && Array.isArray(response.data)) {
          setRows(response.data);
        }
      } catch (error) {
        console.error("Error fetching partners:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSponsors();
  }, []);

  useEffect(() => {
    if (rows.length > 0) {
      rows.forEach((s) =>
        recordSponsorImpression("global_footer", { company: s.title }),
      );
    }
  }, [rows]);

  if (loading || !rows.length) return null;

  if (variant === "navbar") {
    return (
      <div className="hidden lg:flex items-center gap-3 pl-4 ml-2 border-l border-white/[0.08]">
        <span className="text-[10px] uppercase tracking-[0.18em] text-white/40">
          Powered by
        </span>
        <div className="flex items-center gap-3">
          {rows.slice(0, 3).map((s) => (
            <SponsorLogo key={s._id} sponsor={s} compact />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-white/[0.06] py-10 overflow-hidden bg-[#061412]">
      {/* 
          CSS for Infinite Scroll:
          We move the track from 0 to -50%. 
          Because the list is duplicated, -50% looks exactly like 0%, 
          creating a seamless loop.
      */}
      <style>{`
        @keyframes sponsor-scroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .animate-infinite-scroll {
          display: flex;
          width: max-content;
          animation: sponsor-scroll 40s linear infinite;
        }
        .animate-infinite-scroll:hover {
          animation-play-state: paused;
        }
      `}</style>

      <div className="container-x">
        <p className="overline text-center mb-8">
          Powered by our global sponsors
        </p>

        <div className="relative">
          {/* Gradient Fades for the sides */}
          <div className="absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-[#061412] to-transparent z-10"></div>
          <div className="absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-[#061412] to-transparent z-10"></div>

          <div className="overflow-hidden">
            <div className="animate-infinite-scroll">
              {/* Duplicating the list to ensure the loop is seamless */}
              {[...rows, ...rows].map((s, i) => (
                <div key={`${s._id}-${i}`} className="mx-12 flex-shrink-0">
                  <SponsorLogo sponsor={s} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SponsorLogo({ sponsor, compact = false }) {
  const companyName = sponsor.title;
  const logoUrl = sponsor.image;

  const inner = logoUrl ? (
    <img
      src={logoUrl}
      alt={companyName}
      className={`${
        compact ? "h-5" : "h-10 md:h-12"
      } w-auto object-contain opacity-60 hover:opacity-100 transition-all duration-300`}
    />
  ) : (
    <span
      className={`${compact ? "text-xs" : "text-sm"} text-white/65 hover:text-white transition-colors whitespace-nowrap`}
    >
      {companyName}
    </span>
  );

  return (
    <Link
      to={`/brands/${encodeURIComponent(companyName)}`}
      state={{ sponsorData: sponsor }}
      onClick={() =>
        recordSponsorClick("global_footer", { company: companyName })
      }
      className="block"
    >
      {inner}
    </Link>
  );
}
